const TOUCH_MODE = {
  NONE: "none",
  DRAG: "drag",
  ZOOM: "zoom",
  SELECT_MOVE: "select_move",
};
const FONT_FAMILY = "Arial";

export const PIXEL_RATIO = (() => {
  const ctx = document.createElement("canvas").getContext("2d"),
    dpr = window.devicePixelRatio || 1,
    bsr =
      ctx.webkitBackingStorePixelRatio ||
      ctx.mozBackingStorePixelRatio ||
      ctx.msBackingStorePixelRatio ||
      ctx.oBackingStorePixelRatio ||
      ctx.backingStorePixelRatio ||
      1;

  return Math.max(dpr / bsr, 2);
})();
class InteractiveCanvas {
  constructor({
    ctx,
    canvasWidth,
    canvasHeight,
    onSelect,
    onZoom,
    maxBlockX,
    maxBlockY,
    shouldDrawCross,
    selectedTarget,
  }) {
    this.ctx = ctx;
    this.onSelect = onSelect;
    this.onZoom = onZoom;
    this.selectedTarget = selectedTarget;

    this.shouldDrawCross = shouldDrawCross;

    this.bp = 0; //block pixel
    this.minZoom = 0.01;
    this.maxZoom = 2;

    this.zoomScale = 1.05;
    this.renderExtra = 1; //render a extra blocks to cover all
    this.maxBlockX = maxBlockX;
    this.maxBlockY = maxBlockY;

    this.setSize(canvasWidth, canvasHeight);

    this.viewCoord = {
      x: this.maxBlockX / 2,
      y: this.maxBlockY / 2,
    };

    this.setEventListener();

    this.initiated = true;
  }

  cp(value) {
    return value * PIXEL_RATIO;
  }

  setProp(name, value, shouldDraw) {
    this[name] = value;

    if (shouldDraw) {
      this.draw();
    }
  }

  reset() {
    this.viewCoord = {
      x: this.maxBlockX / 2,
      y: this.maxBlockY / 2,
    };
    this.zoomScale = 1.05;
    this.calculateBP();
    this.draw();
  }

  setSize(canvasWidth = 0, canvasHeight = 0, shouldDraw) {
    this.canvasWidth = this.cp(canvasWidth);
    this.canvasHeight = this.cp(canvasHeight);
    this.calculateBP();

    this.ctx.canvas.width = this.canvasWidth;
    this.ctx.canvas.height = this.canvasHeight;

    if (shouldDraw) this.draw();
  }

  calculateBP() {
    const blockRatio = this.maxBlockX / this.maxBlockY;
    const canvasRatio = this.canvasWidth / this.canvasHeight;

    if (blockRatio > canvasRatio) {
      this.bp = this.canvasWidth / this.getScaledMaxBlockX();
    } else {
      this.bp = this.canvasHeight / this.getScaledMaxBlockY();
    }

    this.viewRadius = {
      x: this.getScaledMaxBlockX() / 2,
      y: this.getScaledMaxBlockY() / 2,
    };
    if (this.bp <= 0) {
      this.bp = 1;
    }
  }

  getScaledMaxBlockX() {
    return this.maxBlockX * this.zoomScale;
  }
  getScaledMaxBlockY() {
    return this.maxBlockY * this.zoomScale;
  }

  getClickPoint(e) {
    var x = this.cp(e.pageX - e.target.offsetLeft);
    var y = this.cp(e.pageY - e.target.offsetTop);

    return { x, y };
  }

  onZoomChange(zoomDiff, clickPoint) {
    //decrease zoom value as the zoomScale close to max for a better visual effect
    zoomDiff *= this.zoomScale / this.maxZoom;
    const zx =
      this.canvasWidth * (clickPoint.x / this.canvasWidth) -
      this.canvasWidth / 2;
    const zy =
      this.canvasHeight * (clickPoint.y / this.canvasHeight) -
      this.canvasHeight / 2;

    const prevZoom = this.zoomScale;
    const tempZoom = prevZoom - zoomDiff / 100;
    //calculate view range size
    if (tempZoom <= this.minZoom) return;
    else if (tempZoom >= this.maxZoom) return;
    else this.zoomScale = tempZoom;

    const zoomScale = 1 - this.zoomScale / prevZoom;

    const newx = zx * zoomScale;
    const newy = zy * zoomScale;

    const tx = this.viewCoord.x + newx / this.bp;
    const ty = this.viewCoord.y - newy / this.bp;

    //calcualte new bp
    this.calculateBP();

    //calculate based on old bp and new bp
    this.viewCoord.x = tx;
    this.viewCoord.y = ty;

    this.draw();

    if (this.onZoom) {
      this.onZoom(this.zoomScale);
    }
  }

  getNPFromXY(x, y) {
    const n = Math.floor(
      (x - (this.canvasWidth / 2 - this.viewCoord.x * this.bp)) / this.bp
    );
    const p = Math.floor(
      (this.canvasHeight -
        y -
        (this.canvasHeight / 2 - this.bp * this.viewCoord.y)) /
        this.bp
    );
    return {
      n,
      p,
    };
  }

  getXYFromNP(n, p) {
    const x = n * this.bp + this.canvasWidth / 2 - this.viewCoord.x * this.bp;
    const y =
      -p * this.bp -
      this.bp +
      this.bp * this.viewCoord.y +
      this.canvasHeight / 2;
    return { x, y };
  }

  getPointClient(e) {
    let client = e;
    if (e.touches?.length) {
      client = e.touches[0];
    }
    return client;
  }

  calDistance(e) {
    const x = e.touches[0].pageX - e.touches[1].pageX;
    const y = e.touches[0].pageY - e.touches[1].pageY;
    return Math.sqrt(x * x + y * y);
  }

  setEventListener() {
    let timeElapse = null;
    let previousClicked = {};
    let oldDist = 0;
    let touchMode = TOUCH_MODE.NONE;
    let lastPosition = { x: 0, y: 0 };

    const onDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        const pointClient = this.getPointClient(e);
        const now = Date.now();
        const timesince = now - timeElapse;
        const { x, y } = this.getClickPoint(pointClient);
        if (e.touches?.length === 2) {
          //zoom on pinch
          oldDist = this.calDistance(e);
          touchMode = TOUCH_MODE.ZOOM;
        } else if (
          this.onSelect &&
          timesince < 300 &&
          timesince > 0 &&
          //the previous point and the current point are less than 50px, use to prevent accidentally double touch when zoom
          Math.abs(previousClicked.x - x) + Math.abs(previousClicked.y - y) < 50
        ) {
          //select on double tap
          const { n, p } = this.getNPFromXY(x, y);
          const selected = this.onSelect(p, n);
          if (selected !== false) {
            this.selectedTarget = selected || { neutron: n, proton: p };
            this.draw();
          }
          touchMode = TOUCH_MODE.SELECT_MOVE;
        } else {
          //start move when drag
          previousClicked.x = x;
          previousClicked.y = y;
          touchMode = TOUCH_MODE.DRAG;
          lastPosition.x = pointClient.pageX;
          lastPosition.y = pointClient.pageY;
        }
        timeElapse = Date.now();
      } catch (e) {
        console.error(e);
      }
    };

    const onUp = (e) => {
      e.preventDefault();
      e.stopPropagation();
      touchMode = TOUCH_MODE.NONE;
    };

    const onMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        const pointClient = this.getPointClient(e);
        if (touchMode === TOUCH_MODE.DRAG) {
          //move
          this.viewCoord.x += this.cp(
            (lastPosition.x - pointClient.pageX) / this.bp
          );
          this.viewCoord.y += this.cp(
            (-1 * (lastPosition.y - pointClient.pageY)) / this.bp
          );

          //reset last position
          lastPosition.x = pointClient.pageX;
          lastPosition.y = pointClient.pageY;
          this.draw();
        } else if (touchMode === TOUCH_MODE.SELECT_MOVE) {
          const { x, y } = this.getClickPoint(pointClient);
          const { n, p } = this.getNPFromXY(x, y);
          const selected = this.onSelect(p, n);
          if (selected !== false) {
            this.selectedTarget = selected || { neutron: n, proton: p };
            this.draw();
          }
        } else if (touchMode === TOUCH_MODE.ZOOM) {
          //pinch
          const newDist = this.calDistance(e);
          const zoomDiff = newDist - oldDist;

          const clickPointA = this.getClickPoint(e.touches[0]);
          const clickPointB = this.getClickPoint(e.touches[1]);
          this.onZoomChange(zoomDiff, {
            x: (clickPointA.x + clickPointB.x) / 2,
            y: (clickPointA.y + clickPointB.y) / 2,
          });
          oldDist = newDist;
        }
      } catch (e) {
        console.error(e);
      }
    };
    this.eventHandlers = {
      touchstart: onDown,
      touchend: onUp,
      touchmove: onMove,
      mousewheel: (e) => {
        e.preventDefault();
        e.stopPropagation();

        let dist = e.deltaY;
        if (Math.abs(dist) > 20) {
          dist = 20 * (dist < 0 ? -1 : 1);
        }
        this.onZoomChange(-dist, this.getClickPoint(this.getPointClient(e)));
      },
      mousedown: (e) => {
        onDown(e);
      },
      mouseup: onUp,
      mousemove: onMove,
      mouseleave: onUp,
    };
  }

  //draws
  drawText(text, x, y, font, align = "center") {
    this.ctx.font = `${font || "16px"} ${FONT_FAMILY}`;

    this.ctx.textAlign = align;
    this.ctx.fillText(text, x, y, this.bp * 0.8);
  }

  drawCross(x, y) {
    if (this.shouldDrawCross) {
      const hbp = this.bp / 2;
      const xh = x + hbp;
      const yh = y + hbp;

      this.ctx.beginPath();

      this.ctx.moveTo(xh, y + this.bp);
      this.ctx.lineTo(xh, this.canvasHeight);

      this.ctx.moveTo(xh, y);
      this.ctx.lineTo(xh, 0);

      this.ctx.moveTo(x + this.bp, yh);
      this.ctx.lineTo(this.canvasWidth, yh);

      this.ctx.moveTo(x, yh);
      this.ctx.lineTo(0, yh);
      this.ctx.stroke();

      this.ctx.closePath();
    }
  }

  drawArrow(fromx, fromy, tox, toy) {
    var headlen = this.bp / 5; // length of head in pixels
    var dx = tox - fromx;
    var dy = toy - fromy;
    const angle = Math.atan2(dy, dx);

    this.ctx.beginPath();
    this.ctx.moveTo(fromx, fromy);
    this.ctx.lineTo(tox, toy);
    this.ctx.lineTo(
      tox - headlen * Math.cos(angle - Math.PI / 6),
      toy - headlen * Math.sin(angle - Math.PI / 6)
    );
    this.ctx.moveTo(tox, toy);
    this.ctx.lineTo(
      tox - headlen * Math.cos(angle + Math.PI / 6),
      toy - headlen * Math.sin(angle + Math.PI / 6)
    );

    this.ctx.stroke();
    this.ctx.closePath();
  }

  /**
   * Draws a rounded rectangle using the current state of the canvas.
   * If you omit the last three params, it will draw a rectangle
   * outline with a 5 pixel border radius
   * @param {CanvasRenderingContext2D} ctx
   * @param {Number} x The top left x coordinate
   * @param {Number} y The top left y coordinate
   * @param {Number} width The width of the rectangle
   * @param {Number} height The height of the rectangle
   * @param {Number} [radius = 5] The corner radius; It can also be an object
   *                 to specify different radii for corners
   * @param {Number} [radius.tl = 0] Top left
   * @param {Number} [radius.tr = 0] Top right
   * @param {Number} [radius.br = 0] Bottom right
   * @param {Number} [radius.bl = 0] Bottom left
   * @param {Boolean} [fill = false] Whether to fill the rectangle.
   * @param {Boolean} [stroke = true] Whether to stroke the rectangle.
   */
  roundRect(x, y, width, height, radius, fill, stroke) {
    if (typeof stroke === "undefined") {
      stroke = true;
    }
    if (typeof radius === "undefined") {
      radius = 5;
    }
    if (typeof radius === "number") {
      radius = { tl: radius, tr: radius, br: radius, bl: radius };
    } else {
      var defaultRadius = { tl: 0, tr: 0, br: 0, bl: 0 };
      for (var side in defaultRadius) {
        radius[side] = radius[side] || defaultRadius[side];
      }
    }
    this.ctx.beginPath();
    this.ctx.moveTo(x + radius.tl, y);
    this.ctx.lineTo(x + width - radius.tr, y);
    this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
    this.ctx.lineTo(x + width, y + height - radius.br);
    this.ctx.quadraticCurveTo(
      x + width,
      y + height,
      x + width - radius.br,
      y + height
    );
    this.ctx.lineTo(x + radius.bl, y + height);
    this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
    this.ctx.lineTo(x, y + radius.tl);
    this.ctx.quadraticCurveTo(x, y, x + radius.tl, y);
    this.ctx.closePath();
    if (fill) {
      this.ctx.fill();
    }
    if (stroke) {
      this.ctx.stroke();
    }
  }
}

export default InteractiveCanvas;
